// AuditEventActions — an audit line's ONE registry (ADR-036 §7 — the four kinds;
// mirrors the exemplar `pages/teams/TeamActions.tsx`). A line is a record: no
// primary, no edit — its one act is the operation "See this action's lines" (a
// line that shares a correlation — an import run, a compound request), in the
// peek's door: it narrows the trail to those lines, REPLACING the peek's own
// history entry (the open pushed it, the commit consumes it — FRONTEND-PATTERNS
// §Entity surfaces). `auditEventActs` is the pure registry the shape test walks;
// `useAuditEventActions` resolves it.

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { AuditEvent } from '@ems-app/schemas/audit/responses';
import { resolveActs, type ActionItem, type EntityAct } from '../../../components/patterns/entityRegistry.js';
import { icons } from '../../../components/ui/icons.js';
import { useCan } from '../../../hooks/useCan.js';
import { useEntitlements } from '../../../hooks/useEntitlements.js';

/** What each act does. */
export interface AuditEventActHandlers {
  seeLines: () => void;
}

/** The closed-over facts the rows read. */
export interface AuditEventActFacts {
  /** The line shares a correlation with others. */
  correlated: boolean;
}

/** The line's registry — pure. */
export function auditEventActs(h: AuditEventActHandlers, facts: AuditEventActFacts): ActionItem<'any'>[] {
  return [
    { id: 'see-lines', kind: 'operation', labelKey: 'peek.seeLines', icon: icons.history, run: { handler: h.seeLines }, gate: 'audit:read', order: 10, show: () => facts.correlated },
  ];
}

/** The line's acts — what the peek composes from (nothing until the line is there). */
export function useAuditEventActions(projectId: string, ev: AuditEvent | undefined): EntityAct[] {
  const { t } = useTranslation('audit');
  const navigate = useNavigate();
  const can = useCan(projectId);
  const entitlements = useEntitlements(projectId);
  if (!ev) return [];
  const correlation = ev.correlation_id;
  return resolveActs(
    auditEventActs(
      {
        seeLines: () => {
          const search = new URLSearchParams({ correlation: correlation ?? '' });
          navigate({ pathname: `/p/${projectId}/audit-log`, search: `?${search.toString()}` }, { replace: true });
        },
      },
      { correlated: Boolean(correlation) },
    ),
    { can, entitlements, status: 'any' },
    (key) => t(key),
  );
}
